import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { NavigationProvider, useNavigation } from '@client/contexts/NavigationContext';
import { NigoAnalysisProvider } from '@client/contexts/NigoAnalysisContext';
import {
  useVisibleFormSections,
  useVisibleSectionIndices,
} from '@client/hooks/useFormConditionals';
import {
  FormParameterError,
  FormParameterValues,
  interpolateText,
  useFormParameters,
} from '@client/hooks/useFormParameters';
import { useFormPreview } from '@client/hooks/useFormPreview';
import { savePolicyApplicationFormSubmission } from '@client/hooks/usePolicyApplicationFormSubmission';
import { usePolicyFormDefinition } from '@client/hooks/usePolicyFormDefinition';
import { usePolicyFormMetadata } from '@client/hooks/usePolicyFormMetadata';
import { selectClient } from '@client/model';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FullScreenLoadingOverlay } from '..';
import { FormPreviewMode } from './FormPreviewMode';
import PolicyApplicationDesktopSidebar from './PolicyApplicationDesktopSidebar';
import PolicyApplicationFormSection from './PolicyApplicationFormSection';
import PolicyApplicationMobileProgressBar from './PolicyApplicationMobileProgressBar';
// Forms are now fetched dynamically from API via usePolicyFormDefinition and usePolicyFormMetadata hooks
import { useClientPolicyApplication } from '@client/hooks/useClientPolicyApplication';
import { usePreviousFilledApplicationForm } from '@client/hooks/usePolicyApplicationPrefillForm';
import { usePreviousApplicationPreview } from '@client/hooks/usePreviousApplicationPreview';
import { cleanFormDataForHiddenQuestions } from '@client/utils/formDataUtils';
import { useForm } from 'react-hook-form';
import NigoAnalysisSectionContent from '../NigoAnalysisSection';
import { PreviousApplicationPreviewModal } from './PreviousApplicationPreviewModal';
import { WizardAdminMenu } from './WizardAdminMenu';
import { PreUnderwritingFormCTA } from '@client/features/underwriting-eligibility';

interface PolicyApplicationFormProps {
  formId: string;
  clientId: string;
  clientPolicyApplicationId: string;
  forClientId: string;
}

interface FormParameterValidationErrorProps {
  errors: FormParameterError[];
  missingRequired: string[];
}

function FormParameterValidationError({
  errors,
  missingRequired,
}: FormParameterValidationErrorProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="max-w-md text-center">
        <h2 className="mb-4 text-xl font-semibold text-red-900">Form Configuration Error</h2>
        <p className="mb-4 text-gray-700">
          This form requires additional information to be provided in the URL.
        </p>
        <div className="space-y-2 text-left">
          {errors.map((error, index) => (
            <div
              key={index}
              className="rounded-md bg-red-50 p-3"
            >
              <p className="text-sm text-red-800">{error.message}</p>
            </div>
          ))}
        </div>
        {missingRequired.length > 0 && (
          <div className="mt-4 text-sm text-gray-700">
            <p>Missing required parameters: {missingRequired.join(', ')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function PolicyApplicationForm({
  formId,
  clientId,
  clientPolicyApplicationId,
  forClientId,
}: PolicyApplicationFormProps) {
  const [unrestrictedNavEnabled, setUnrestrictedNavEnabled] = useState(false);
  //Get latest form definition from API (bypass + update cache) to ensure we have the latest changes for the e-app
  const { data: originalFormDefinition, isLoading: isLoadingFormDefinition } =
    usePolicyFormDefinition(formId, true);

  const {
    activeDefinition: formDefinition,
    setOverrideDefinition,
    hasOverride,
  } = useFormPreview(formId, originalFormDefinition);

  const { parameters, errors, isValid, missingRequired } = useFormParameters(
    formDefinition?.requiredParameters ?? [],
  );

  const previousApplicationData = usePreviousFilledApplicationForm(
    forClientId,
    clientPolicyApplicationId,
    formId,
  );

  // Show loading state while fetching form from API
  if (isLoadingFormDefinition) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="text-primary mx-auto mb-4 h-12 w-12 animate-spin" />
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Loading form...</h2>
          <p className="text-gray-700">Please wait while we fetch your application form.</p>
        </div>
      </div>
    );
  }

  // Show error state if form not found after loading
  if (formDefinition === undefined || Object.keys(formDefinition).length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Form not found</h2>
          <p className="text-gray-700">The requested form could not be loaded.</p>
        </div>
      </div>
    );
  }

  // Show parameter validation errors
  if (!isValid) {
    return (
      <FormParameterValidationError
        errors={errors}
        missingRequired={missingRequired}
      />
    );
  }

  return (
    <>
      <NavigationProvider
        formDefinition={formDefinition}
        formId={formId}
        clientId={clientId}
        clientPolicyApplicationId={clientPolicyApplicationId}
        parameters={parameters}
        forClientId={forClientId}
        wizardUnrestrictedNavEnabled={unrestrictedNavEnabled}
      >
        <FormContent
          formId={formId}
          clientId={clientId}
          parameters={parameters}
          forClientId={forClientId}
          previousApplicationData={previousApplicationData}
          originalFormDefinition={originalFormDefinition}
          setOverrideDefinition={setOverrideDefinition}
          hasOverride={hasOverride}
          unrestrictedNavEnabled={unrestrictedNavEnabled}
          setUnrestrictedNavEnabled={setUnrestrictedNavEnabled}
        />
      </NavigationProvider>
    </>
  );
}

function FormContent({
  formId,
  clientId,
  parameters = {},
  forClientId,
  previousApplicationData,
  hasPreviewBanner = false,
  originalFormDefinition,
  setOverrideDefinition,
  hasOverride,
  unrestrictedNavEnabled,
  setUnrestrictedNavEnabled,
}: {
  formId: string;
  clientId: string;
  parameters?: FormParameterValues;
  forClientId: string;
  previousApplicationData: {
    clientPolicyApplicationId: string;
    policyApplicationFormId: string | undefined;
    stepType: PolicyApplicationStepType;
    status: PolicyApplicationStep['status'];
    submissionData: PolicyFormSubmissionData;
    visitedSubsections: string[] | undefined;
    submissionDateTime: Date | undefined;
    stateOfResidenceAbbreviation: string | undefined;
    fundingPolicyType: FundingPolicyType;
  }[];
  hasPreviewBanner?: boolean;
  originalFormDefinition: PolicyFormDefinition | undefined;
  setOverrideDefinition: (definition: PolicyFormDefinition | undefined) => void;
  hasOverride: boolean;
  unrestrictedNavEnabled: boolean;
  setUnrestrictedNavEnabled: (enabled: boolean) => void;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const client = useSelector(selectClient);

  const replaceClientNames = (text: string | undefined): string => {
    if (!text) return '';
    return text
      .replace('{clientFullName}', client?.clientFullName ?? '')
      .replace('{clientPartnerFullName}', client?.mutableClientPartner?.clientFullName ?? 'spouse');
  };

  const {
    state,
    actions,
    computed,
    formDefinition,
    getCurrentSubsection,
    getCurrentSection,
    clientPolicyApplicationId,
    animationDirection,
    validationState,
    navigationService,
  } = useNavigation();
  const {
    fundingSourceDef,
    policyFundingSource,
    policyType,
    applicationFormId: appFormId,
  } = useClientPolicyApplication();

  // Get form metadata from either cache (API) or static definitions
  const { data: metadata } = usePolicyFormMetadata(formId);
  const formTitle = metadata?.title ?? 'Application Form';

  const currentSection = getCurrentSection();
  const currentSubsection = getCurrentSubsection();

  const [isPreviewBannerVisible, setIsPreviewBannerVisible] = useState(false);
  const [showFormPreview, setShowFormPreview] = useState(false);
  const [showNigoAnalysisView, setShowNigoAnalysisView] = useState(false);

  const visibleSectionsData = useVisibleFormSections(formDefinition, state.formData, parameters);
  const visibleSections: PolicyFormSection[] = visibleSectionsData.map(data => ({
    ...data.section,
    subsections: data.subsections,
  }));

  const { control, watch } = useForm<{ previousApplicationId: string }>({
    defaultValues: { previousApplicationId: '' },
  });

  const crossFadeVariants = {
    enter: (direction: number) => ({
      opacity: 0,
      y: direction > 0 ? 10 : -10, // Subtle movement: Next from below, Previous from above
    }),
    center: {
      zIndex: 1,
      opacity: 1,
      y: 0,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      opacity: 0,
      y: direction < 0 ? -10 : 10, // Subtle movement: Next exits up, Previous exits down
    }),
  };

  const handleSubmissionChange = (questionRef: string, value: PolicyFormSubmissionDataValue) => {
    actions.updateFormData(questionRef, value);
  };

  const handleSectionChange = (visibleSectionIndex: number, visibleSubsectionIndex: number) => {
    // Get the target section from visibleSections (not the full form definition)
    const targetVisibleSection = visibleSections[visibleSectionIndex];
    const targetSubsection = targetVisibleSection?.subsections[visibleSubsectionIndex];

    if (targetSubsection && targetVisibleSection) {
      // Find the absolute section index in the original form definition
      const absoluteSectionIndex = formDefinition.sections.findIndex(
        section => section.id === targetVisibleSection.id,
      );

      if (absoluteSectionIndex >= 0) {
        // Find the absolute subsection index within the original section
        const originalSection = formDefinition.sections[absoluteSectionIndex];
        const absoluteSubsectionIndex = originalSection.subsections.findIndex(
          subsection => subsection.id === targetSubsection.id,
        );

        if (absoluteSubsectionIndex >= 0) {
          const position = {
            sectionIndex: absoluteSectionIndex,
            subsectionIndex: absoluteSubsectionIndex,
            subsectionId: targetSubsection.id,
          };
          setShowNigoAnalysisView(false);
          actions.navigateTo(position);
        }
      }
    }
  };

  // Check if there are any validation errors preventing navigation
  const hasValidationErrors = useCallback(() => {
    // fieldValidationErrors: Contains both format errors (email, phone) and required field errors
    // computed.canGoNext: Checks if all required fields have values (from NavigationService)
    return validationState.fieldValidationErrors.size > 0 || !computed.canGoNext;
  }, [validationState.fieldValidationErrors, computed.canGoNext]);

  const handleNext = useCallback(() => {
    const hasErrors = hasValidationErrors();

    if (hasErrors && !validationState.showValidationErrors) {
      // show warnings but do NOT navigate
      actions.showValidationErrors();
      return;
    }

    if (currentSubsection) {
      actions.markVisited(currentSubsection.id);
    }

    // allow navigation with force = true
    actions.navigateNext(true);
    // Check if this is the last step and form is complete
    if (computed.isLastStep && state.progress?.isFormComplete) {
      setShowNigoAnalysisView(true);
    }
  }, [
    currentSubsection,
    actions,
    hasValidationErrors,
    visibleSectionsData,
    currentSection,
    formDefinition.sections,
    computed.canGoNext,
    computed.isLastStep,
    state.progress?.isFormComplete,
    validationState.fieldValidationErrors,
    validationState.showValidationErrors,
  ]);

  const handlePrevious = useCallback(() => {
    if (currentSubsection) {
      actions.markVisited(currentSubsection.id);
    }

    // TODO: Update navigation to skip hidden sections/subsections based on visibleSections
    // This requires updating NavigationContext to be aware of conditional visibility
    actions.navigatePrevious();
  }, [currentSubsection, actions, visibleSectionsData, currentSection, formDefinition.sections]);

  const handleSaveAndClose = () => {
    if (state.currentPosition && currentSubsection) {
      actions.markVisited(currentSubsection.id);
      const updatedVisited = new Set(state.visitedSubsections);
      updatedVisited.add(currentSubsection.id);
      const visitedArray = Array.from(updatedVisited);
      // Clean form data to remove hidden fields before saving
      const cleanedFormData = cleanFormDataForHiddenQuestions(
        state.formData,
        formDefinition,
        parameters,
      );

      const isCompleted = state.progress?.isFormComplete;

      savePolicyApplicationFormSubmission(
        clientId,
        clientPolicyApplicationId,
        formId,
        cleanedFormData,
        isCompleted ? 'completed' : 'in_progress',
        visitedArray,
        dispatch,
        forClientId,
      );
      navigate(`/clients/${clientId}/policy-application/${clientPolicyApplicationId}`);
    }
  };

  // Handle Enter key press to trigger Next button
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Check if Enter key is pressed and Next button is enabled
      if (
        event.key === 'Enter' &&
        computed.canGoNext &&
        !computed.isSaving &&
        // Don't trigger if user is in a textarea
        event.target instanceof HTMLElement &&
        event.target.tagName !== 'TEXTAREA'
      ) {
        // Prevent default form submission behavior
        event.preventDefault();
        handleNext();
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyPress);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [computed.canGoNext, computed.isSaving, handleNext]);

  const completedSubsections = useMemo(
    () => state.progress?.completedSubsections || new Set<string>(),
    [state.progress?.completedSubsections],
  );

  // Convert absolute indices to visible section indices using the custom hook
  const { visibleSectionIndex, visibleSubsectionIndex } = useVisibleSectionIndices(
    currentSection,
    currentSubsection,
    visibleSectionsData,
  );

  const {
    openModal,
    setOpenModal,
    selectedApplicationData,
    handleSelectApplication,
    handleAcceptApplicationPrefil,
  } = usePreviousApplicationPreview(previousApplicationData, state, actions, parameters);

  if (computed.isLoading) {
    return <FullScreenLoadingOverlay isLoading={true} />;
  }

  const showErrors = validationState.showValidationErrors && hasValidationErrors();

  const shouldHideSubmitButton =
    !showErrors && computed.isLastStep && !state.progress?.isFormComplete;

  return (
    <div
      className={cn(
        'flex h-screen flex-col overflow-hidden bg-gray-50 md:flex-row',
        isPreviewBannerVisible && 'pt-8',
      )}
    >
      <PolicyApplicationMobileProgressBar
        currentSectionIndex={visibleSectionIndex}
        currentSubsectionIndex={visibleSubsectionIndex}
        completedSubsections={completedSubsections}
        visibleSections={visibleSections}
        parameters={parameters}
      />

      <PolicyApplicationDesktopSidebar
        formTitle={formTitle}
        formDefinition={formDefinition}
        product={fundingSourceDef?.label || metadata?.product || 'Application'}
        currentSectionIndex={state.currentPosition?.sectionIndex || 0}
        currentSubsectionIndex={state.currentPosition?.subsectionIndex || 0}
        completedSubsections={completedSubsections}
        onSectionChange={handleSectionChange}
        clientPolicyApplicationId={clientPolicyApplicationId}
        visibleSections={visibleSections}
        parameters={parameters}
      />

      <div className="flex-1 overflow-auto p-4 md:p-8">
        <div className="mx-auto max-w-4xl">
          <NigoAnalysisProvider
            showNigoAnalysisView={showNigoAnalysisView}
            setShowNigoAnalysisView={setShowNigoAnalysisView}
            parameters={parameters}
          >
            <NigoAnalysisSectionContent />
          </NigoAnalysisProvider>
          {!showNigoAnalysisView && (
            <>
              {/* Section Title */}
              {currentSection && (
                <div className="mb-4 flex items-center justify-between md:mb-6">
                  <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
                    {interpolateText(currentSection.title, parameters)}
                  </h1>

                  <div className="flex items-center gap-3">
                    <WizardAdminMenu
                      onOpenFormPreview={() => setShowFormPreview(true)}
                      onOpenPreviousApplications={() => setOpenModal(true)}
                      unrestrictedNavEnabled={unrestrictedNavEnabled}
                      onToggleUnrestrictedNav={setUnrestrictedNavEnabled}
                      hasPreviousApplications={previousApplicationData.length > 0}
                    />
                  </div>
                </div>
              )}
              {/* Subsection Progress Bar */}
              {currentSection &&
                (() => {
                  // Find the current visible section that matches the current section
                  const currentVisibleSection = visibleSections.find(
                    section => section.id === currentSection.id,
                  );

                  if (!currentVisibleSection) return null;

                  return (
                    <div className="mb-6 hidden md:mb-8 md:block">
                      <div className="flex space-x-2">
                        {currentVisibleSection.subsections.map((subsection, index) => (
                          <div
                            key={subsection.id}
                            className="flex-1"
                          >
                            <div
                              className={cn(
                                'h-2 rounded-full',
                                state.progress?.completedSubsections.has(subsection.id)
                                  ? 'bg-primary'
                                  : index === visibleSubsectionIndex
                                    ? state.progress?.completedSubsections.has(subsection.id)
                                      ? 'bg-primary'
                                      : 'bg-primary-200'
                                    : 'bg-gray-200',
                              )}
                            />
                            <div className="mt-2 flex items-center gap-2">
                              {index === visibleSubsectionIndex && (
                                <div className="bg-primary h-2 w-2 flex-shrink-0 rounded-full" />
                              )}
                              <span
                                className={cn(
                                  'text-xs',
                                  index === visibleSubsectionIndex
                                    ? 'text-primary-dark font-semibold'
                                    : 'text-gray-700',
                                )}
                              >
                                {replaceClientNames(subsection.title)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              {/* Form Content */}
              <Card className="p-4 md:p-8">
                <div className="relative">
                  <AnimatePresence
                    mode="wait"
                    custom={animationDirection}
                  >
                    {currentSubsection && (
                      <motion.div
                        key={currentSubsection.id}
                        custom={animationDirection}
                        variants={crossFadeVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                          duration: 0.3,
                          ease: 'easeInOut',
                        }}
                      >
                        <div className="relative">
                          {metadata?.formType === 'pre-underwriting-form' &&
                            metadata?.carrier === 'Nationwide' && (
                              <PreUnderwritingFormCTA
                                formData={state.formData}
                                fundingPolicyType={policyType!}
                              />
                            )}
                          <PolicyApplicationFormSection
                            subsection={currentSubsection}
                            formData={state.formData}
                            questionRegistry={formDefinition.questions}
                            onSubmissionChange={handleSubmissionChange}
                            parameters={parameters}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Navigation Buttons */}
                <div className="mt-6 space-y-4 pt-6 md:mt-8">
                  {/* Validation Error Alert */}
                  {((validationState.showValidationErrors && hasValidationErrors()) ||
                    (currentSubsection &&
                      navigationService.isSubsectionWarning(currentSubsection.id))) && (
                    <Alert className="border-0 bg-yellow-50 text-yellow-800">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        This section has required fields that are still incomplete. You can move
                        forward and come back later.
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="flex flex-col-reverse justify-between gap-4 sm:flex-row">
                    <Button
                      variant="outline"
                      onClick={handleSaveAndClose}
                      disabled={computed.isSaving}
                      className="w-full sm:w-auto"
                    >
                      Save & Close
                    </Button>
                    <div className="flex flex-col-reverse gap-4 sm:flex-row">
                      <Button
                        variant="outline"
                        onClick={handlePrevious}
                        disabled={!computed.canGoPrevious}
                        className="w-full sm:w-24"
                      >
                        Previous
                      </Button>

                      {shouldHideSubmitButton ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  disabled={true}
                                  className={cn('w-auto min-w-[96px] px-4 sm:min-w-[120px]')}
                                >
                                  Review Application
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Please complete all required sections to submit.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <Button
                          onClick={handleNext}
                          disabled={computed.isSaving}
                          className={cn('w-auto min-w-[96px] px-4 sm:min-w-[120px]')}
                        >
                          {computed.isSaving ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            </>
                          ) : showErrors ? (
                            'Continue Anyway'
                          ) : computed.isLastStep ? (
                            'Review Application'
                          ) : (
                            'Next'
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>

      <PreviousApplicationPreviewModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onAccept={handleAcceptApplicationPrefil}
        previousApplicationData={previousApplicationData}
        selectedApplicationData={selectedApplicationData}
        onSelectApplication={handleSelectApplication}
      />

      {originalFormDefinition && (
        <FormPreviewMode
          currentFormDefinition={formDefinition}
          onFormDefinitionChange={setOverrideDefinition}
          hasOverride={hasOverride}
          originalFormDefinition={originalFormDefinition}
          onBannerVisibilityChange={setIsPreviewBannerVisible}
          isOpen={showFormPreview}
          onOpenChange={setShowFormPreview}
        />
      )}
    </div>
  );
}

export default PolicyApplicationForm;
